/**
 * Login Component
 * 
 * Standalone login page with authentication - Exact UX design implementation
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(formData.email, formData.password, formData.rememberMe);
      navigate('/');
    } catch (error) {
      console.error('Login failed:', error);
      setError(error.response?.data?.detail || t('login.errorInvalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Left Panel - Hero Section with Gradient */}
      <section 
        className="flex-1 flex items-center justify-center p-12 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #007E8F 0%, #00C6B3 50%, #6FFFE0 100%)'
        }}
      >
        {/* Background Decoration */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-xl">
          {/* Logo Section */}
          <div className="mb-12">
            <div className="flex items-center space-x-4 mb-8">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-2xl">
                <i className="fa-solid fa-robot text-white text-3xl"></i>
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">{t('login.hero.title')}</h1>
                <p className="text-white/80 text-sm">{t('login.hero.subtitle')}</p>
              </div>
            </div>
          </div>
          
          {/* Hero Content */}
          <div className="mb-12">
            <p className="text-xl text-white/90 mb-8 leading-relaxed">{t('login.hero.description')}</p>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <i className="fa-solid fa-bolt text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">{t('login.hero.feature1Title')}</h3>
                  <p className="text-white/80 text-sm">{t('login.hero.feature1Desc')}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <i className="fa-solid fa-brain text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">{t('login.hero.feature2Title')}</h3>
                  <p className="text-white/80 text-sm">{t('login.hero.feature2Desc')}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <i className="fa-solid fa-shield-halved text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">{t('login.hero.feature3Title')}</h3>
                  <p className="text-white/80 text-sm">{t('login.hero.feature3Desc')}</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Stats Section */}
          <div className="grid grid-cols-3 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">15+</div>
              <div className="text-white/80 text-sm">{t('login.hero.stat1')}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">∞</div>
              <div className="text-white/80 text-sm">{t('login.hero.stat2')}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">24/7</div>
              <div className="text-white/80 text-sm">{t('login.hero.stat3')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Right Panel - Login Form */}
      <section className="w-[600px] bg-white flex items-center justify-center shadow-2xl overflow-y-auto">
        <div className="w-full max-w-md py-12 px-8">
          {/* Login Header */}
          <div className="mb-10 text-center">
            <h2 className="text-4xl font-bold text-[#212121] mb-3">{t('login.title')}</h2>
            <p className="text-[#4A4A4A] text-lg">{t('login.subtitle')}</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl">
              <div className="flex items-center">
                <i className="fas fa-exclamation-circle text-red-600 mr-3"></i>
                <p className="text-sm text-red-600 font-medium">{error}</p>
              </div>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div>
              <label className="block mb-2">
                <span className="text-sm font-bold text-[#212121]">{t('login.email')} <span className="text-[#00C6B3]">*</span></span>
              </label>
              <div className="relative">
                <i className="fa-solid fa-envelope absolute left-4 top-4 text-[#B0B9C1]"></i>
                <input 
                  type="email" 
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                  className="w-full pl-12 pr-4 py-4 border-2 border-[#B0B9C1]/30 rounded-xl focus:ring-2 focus:ring-[#00C6B3] focus:border-transparent text-[#212121] placeholder-[#B0B9C1] transition-all"
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className="block mb-2">
                <span className="text-sm font-bold text-[#212121]">{t('login.password')} <span className="text-[#00C6B3]">*</span></span>
              </label>
              <div className="relative">
                <i className="fa-solid fa-lock absolute left-4 top-4 text-[#B0B9C1]"></i>
                <input 
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={t('login.password')}
                  className="w-full pl-12 pr-12 py-4 border-2 border-[#B0B9C1]/30 rounded-xl focus:ring-2 focus:ring-[#00C6B3] focus:border-transparent text-[#212121] placeholder-[#B0B9C1] transition-all"
                  required
                />
                <i 
                  onClick={() => setShowPassword(!showPassword)}
                  className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'} password-toggle absolute right-4 top-4 text-[#B0B9C1] hover:text-[#00C6B3] transition-colors cursor-pointer`}
                ></i>
              </div>
            </div>

            {/* Remember & Forgot */}
            <div className="flex items-center justify-between">
              <label className="flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleChange}
                  className="w-5 h-5 text-[#00C6B3] border-[#B0B9C1]/30 rounded focus:ring-[#00C6B3] cursor-pointer"
                />
                <span className="ml-3 text-sm font-medium text-[#212121]">{t('login.rememberMe')}</span>
              </label>
              <a href="#" className="text-sm font-semibold text-[#00C6B3] hover:text-[#007E8F] transition-colors">
                {t('login.forgotPassword')}
              </a>
            </div>

            {/* Submit Button */}
            <button 
              type="submit" 
              disabled={loading}
              className="w-full py-4 hover:opacity-90 text-white rounded-xl text-lg font-bold shadow-lg transition-all transform hover:scale-[1.02] flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: 'linear-gradient(135deg, #007E8F 0%, #00C6B3 100%)'
              }}
            >
              <i className="fa-solid fa-right-to-bracket mr-3"></i>
              {loading ? t('login.signingIn') : t('login.signIn')}
            </button>

            {/* Divider */}
            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#B0B9C1]/30"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-[#4A4A4A] font-medium">{t('login.orContinueWith')}</span>
              </div>
            </div>

            {/* Social Login */}
            <div className="grid grid-cols-3 gap-4">
              <button 
                type="button" 
                className="py-3 bg-white border-2 border-[#B0B9C1]/30 hover:border-[#00C6B3] hover:bg-[#F5F6F8] rounded-xl transition-all flex items-center justify-center"
              >
                <i className="fa-brands fa-google text-xl text-[#4A4A4A]"></i>
              </button>
              <button 
                type="button" 
                className="py-3 bg-white border-2 border-[#B0B9C1]/30 hover:border-[#00C6B3] hover:bg-[#F5F6F8] rounded-xl transition-all flex items-center justify-center"
              >
                <i className="fa-brands fa-github text-xl text-[#4A4A4A]"></i>
              </button>
              <button 
                type="button" 
                className="py-3 bg-white border-2 border-[#B0B9C1]/30 hover:border-[#00C6B3] hover:bg-[#F5F6F8] rounded-xl transition-all flex items-center justify-center"
              >
                <i className="fa-brands fa-microsoft text-xl text-[#4A4A4A]"></i>
              </button>
            </div>
          </form>

          {/* Signup Link */}
          <div className="mt-8 text-center">
            <p className="text-[#4A4A4A]">
              {t('login.noAccount')}
              <a href="/register" className="font-bold text-[#00C6B3] hover:text-[#007E8F] transition-colors ml-1">
                {t('login.createAccount')}
              </a>
            </p>
          </div>

          {/* Security Badge */}
          <div className="mt-10 p-4 bg-[#F5F6F8] rounded-xl border border-[#B0B9C1]/20">
            <div className="flex items-center justify-center space-x-3 text-sm text-[#4A4A4A]">
              <i className="fa-solid fa-shield-halved text-[#00C6B3]"></i>
              <span>{t('login.securityBadge')}</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

